import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import {
  INDICATOR,
  INDICATOR_GROUP,
  indicator_group_names,
  indicator_names,
} from '@voladeum/client';
import { CandleData } from 'client/voladeum/candles/types';
import { selectSettings } from 'ducks/settings/selectors';
import { useAppNavigate } from 'hooks/useAppNavigate';
import { useIndicatorContext } from 'pages/context/IndicatorContext';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppSelector } from 'store/hooks';
import { parseQuery } from 'utils/queries';

import { Card } from 'components/cards/Card';
import { Tabs } from 'components/tabs/Tabs';

import 'chartjs-adapter-moment';

import { indicators_advanced, indicators_oscillator } from '../../constants';

import { AccGraph } from './AccGraph';
import { ATRGraph } from './ATRGraph';
import { CCIGraph } from './CCIGraph';
import { MACDGraph } from './MACDGraph';
import { OBVGraph } from './OBVGraph';
import { OscillatorsGraph } from './OscillatorsGraph';
import { STDGraph } from './STDGraph';

import cn from './IndicatorsGraph.module.scss';

export const AdvancedGraph = ({
  candles,
  visible,
}: {
  candles: CandleData[];
  visible: { start: number; end: number };
}) => {
  const { search } = useLocation();
  const navigate = useAppNavigate();
  const { indicator_group_id, indicator_id } = parseQuery(search);
  const { loading_component } = useAppSelector(selectSettings);
  const { indicators: strategy_indicators } = useStrategyContext();
  const { indicators: signal_indicators } = useIndicatorContext();

  const onClickOscillator = useCallback(
    (indicator_group_id: INDICATOR_GROUP) => {
      navigate.update({ indicator_group_id, indicator_id: null });
    },
    [navigate]
  );

  const onClickAdvanced = useCallback(
    (indicator_id: INDICATOR) => {
      navigate.update({ indicator_id, indicator_group_id: null });
    },
    [navigate]
  );

  const combined_indicators = useMemo(
    () => [...strategy_indicators, ...signal_indicators],
    [signal_indicators, strategy_indicators]
  );

  const advanced_graph = useMemo(() => {
    return {
      macd: !combined_indicators.find((ind) => ind.indicator_id === INDICATOR.MACD),
      cci: !combined_indicators.find((ind) => ind.indicator_id === INDICATOR.CCI),
      obv: !combined_indicators.find((ind) => ind.indicator_id === INDICATOR.OBV),
      acc: !combined_indicators.find((ind) => ind.indicator_id === INDICATOR.ACCUMULATION_DIST),
      std: !combined_indicators.find((ind) => ind.indicator_id === INDICATOR.STANDARD_DEV),
      atr: !combined_indicators.find((ind) => ind.indicator_id === INDICATOR.ATR),
    };
  }, [combined_indicators]);

  const active_indicator_group_id = Number(indicator_group_id || 0);
  const active_indicator_id = Number(indicator_id);
  const default_id = active_indicator_group_id === 0 && !indicator_id;
  const active_oscillator = active_indicator_group_id === INDICATOR_GROUP.OSCILLATOR || default_id;

  const indicators_hidden = new Set<number>([...indicators_oscillator, ...indicators_advanced]);
  const is_visible = combined_indicators.some((indicator) =>
    indicators_hidden.has(indicator.indicator_id)
  );

  if (!is_visible) return null;

  return (
    <Card
      loading={loading_component}
      padding="0px 16px 16px 16px"
      minHeight="100px"
      maxHeight="30%"
      direction="column"
    >
      <Tabs padding="16px 0px 0px 0px">
        <Tabs.Tab
          label={indicator_group_names[INDICATOR_GROUP.OSCILLATOR]}
          onClick={() => onClickOscillator(INDICATOR_GROUP.OSCILLATOR)}
          active={active_oscillator}
        />
        <Tabs.Tab
          label={indicator_names[INDICATOR.MACD]}
          onClick={() => onClickAdvanced(INDICATOR.MACD)}
          active={active_indicator_id === INDICATOR.MACD}
          is_hidden={advanced_graph.macd}
        />
        <Tabs.Tab
          label={indicator_names[INDICATOR.CCI]}
          onClick={() => onClickAdvanced(INDICATOR.CCI)}
          active={active_indicator_id === INDICATOR.CCI}
          is_hidden={advanced_graph.cci}
        />
        <Tabs.Tab
          label={indicator_names[INDICATOR.OBV]}
          onClick={() => onClickAdvanced(INDICATOR.OBV)}
          active={active_indicator_id === INDICATOR.OBV}
          is_hidden={advanced_graph.obv}
        />
        <Tabs.Tab
          label={indicator_names[INDICATOR.ATR]}
          onClick={() => onClickAdvanced(INDICATOR.ATR)}
          active={active_indicator_id === INDICATOR.ATR}
          is_hidden={advanced_graph.atr}
        />
        <Tabs.Tab
          label={indicator_names[INDICATOR.ACCUMULATION_DIST]}
          onClick={() => onClickAdvanced(INDICATOR.ACCUMULATION_DIST)}
          active={active_indicator_id === INDICATOR.ACCUMULATION_DIST}
          is_hidden={advanced_graph.acc}
        />
        <Tabs.Tab
          label={indicator_names[INDICATOR.STANDARD_DEV]}
          onClick={() => onClickAdvanced(INDICATOR.STANDARD_DEV)}
          active={active_indicator_id === INDICATOR.STANDARD_DEV}
          is_hidden={advanced_graph.std}
        />
      </Tabs>
      <div className={cn.flex}>
        {active_oscillator && <OscillatorsGraph candles={candles} visible={visible} />}
        {active_indicator_id === INDICATOR.MACD && (
          <MACDGraph candles={candles} visible={visible} />
        )}
        {active_indicator_id === INDICATOR.CCI && <CCIGraph candles={candles} visible={visible} />}
        {active_indicator_id === INDICATOR.OBV && <OBVGraph candles={candles} visible={visible} />}
        {active_indicator_id === INDICATOR.ACCUMULATION_DIST && (
          <AccGraph candles={candles} visible={visible} />
        )}
        {active_indicator_id === INDICATOR.ATR && <ATRGraph candles={candles} visible={visible} />}
        {active_indicator_id === INDICATOR.STANDARD_DEV && (
          <STDGraph candles={candles} visible={visible} />
        )}
      </div>
    </Card>
  );
};
